import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import Edit from "@material-ui/icons/Edit";
import Delete from "@material-ui/icons/Delete";

import MainContainer from "../../components/MainContainer";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import ContactTagListModal from "../../components/ContactTagListModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// Reducer para manipular o estado das tags
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_TAGS":
      return [...state, ...action.payload];
    case "UPDATE_TAGS":
      const tag = action.payload;
      const tagIndex = state.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) {
        state[tagIndex] = tag;
        return [...state];
      } else {
        return [tag, ...state];
      }
    case "DELETE_TAGS":
      const tagId = action.payload;
      return state.filter((tag) => tag.id !== tagId);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    maxHeight: "70vh",
    ...theme.scrollbarStyles,
  },
  headerCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    background: "#fff",
    border: "1px solid #e7edf7",
    borderRadius: 18,
    padding: theme.spacing(2.5, 3),
    marginBottom: theme.spacing(2),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  titleText: {
    color: "#152b51",
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1.2,
  },
  countPill: {
    background: "#eaf2ff",
    color: "#1454e9",
    borderRadius: 20,
    fontWeight: 700,
    padding: "6px 13px",
  },
  subtitle: {
    color: "#70809a",
    fontSize: 14,
    marginTop: theme.spacing(0.7),
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  searchField: {
    minWidth: 230,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: "#f9fbff",
    },
  },
  addButton: {
    minHeight: 41,
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "none",
  },
  listPaper: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "auto",
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #e7edf7",
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  gridHeader: {
    display: "grid",
    gridTemplateColumns: "85px minmax(160px, 1fr) minmax(140px, 1fr) 122px",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: 560,
    padding: theme.spacing(1.25, 2),
    background: "#f6f9fe",
    borderRadius: 10,
    color: "#667790",
    fontWeight: 800,
    fontSize: 12,
    marginBottom: theme.spacing(1.5),
  },
  cardContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    minWidth: 560,
  },
  card: {
    display: "grid",
    gridTemplateColumns: "85px minmax(160px, 1fr) minmax(140px, 1fr) 122px",
    alignItems: "center",
    gap: theme.spacing(2),
    minHeight: 70,
    padding: theme.spacing(1.5, 2),
    background: "#fff",
    border: "1px solid #edf1f7",
    borderLeft: "6px solid var(--tag-color)",
    borderRadius: 12,
    boxShadow: "0 3px 12px rgba(15, 23, 42, 0.04)",
    '&:hover': {
      background: "#fafcff",
      boxShadow: "0 5px 18px rgba(15, 23, 42, 0.08)",
    },
  },
  colorDot: {
    height: 22,
    width: 22,
    borderRadius: "50%",
    background: "var(--tag-color)",
    display: "inline-block",
  },
  label: {
    fontWeight: 700,
    color: "#1a2b4c",
    fontSize: 15,
    overflowWrap: "anywhere",
  },
  contactsSection: {
    color: "#687994",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  editButton: {
    background: "#eef4ff",
    color: "#315c98",
    '&:hover': { background: "#dfeaff" },
  },
  deleteButton: {
    background: "#fff0f0",
    color: "#e04444",
    '&:hover': { background: "#ffe2e2" },
  },
}));
const Tags = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [selectedTagContacts, setSelectedTagContacts] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTagName, setSelectedTagName] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const pageNumberRef = useRef(1);

  useEffect(() => {
    const fetchMoreTags = async () => {
      try {
        const { data } = await api.get("/tags/", {
          params: { searchParam, pageNumber, kanban: 0 },
        });
        dispatch({ type: "LOAD_TAGS", payload: data.tags });
        setHasMore(data.hasMore);
        setLoading(false);
      } catch (err) {
        toastError(err);
      }
    };

    if (pageNumber > 0) {
      setLoading(true);
      fetchMoreTags();
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const onCompanyTags = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };

    socket.on(`company${user.companyId}-tag`, onCompanyTags);
    return () => {
      socket.off(`company${user.companyId}-tag`, onCompanyTags);
    };
  }, [socket, user.companyId]);

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    const newSearchParam = event.target.value.toLowerCase();
    setSearchParam(newSearchParam);
    setPageNumber(1);
    dispatch({ type: "RESET" });
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedTagContacts([]);
    setSelectedTagName("");
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer className={classes.mainContainer}>
      {contactModalOpen && (
        <ContactTagListModal
          open={contactModalOpen}
          onClose={handleCloseContactModal}
          tag={selectedTagName}
        />
      )}

      <ConfirmationModal
        title={deletingTag && `${i18n.t("tags.confirmationModal.deleteTitle")}`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tags.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        aria-labelledby="form-dialog-title"
        tagId={selectedTag && selectedTag.id}
        kanban={0}
      />

      <div className={classes.headerCard}>
        <div>
          <div className={classes.titleRow}>
            <span className={classes.titleText}>Etiquetas</span>
            <span className={classes.countPill}>{tags.length}</span>
          </div>
          <div className={classes.subtitle}>
            Organize e identifique seus contatos e atendimentos por etiquetas.
          </div>
        </div>
        <div className={classes.headerActions}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder="Pesquisar etiquetas..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#7184a1" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.addButton}
            onClick={handleOpenTagModal}
          >
            {i18n.t("tags.buttons.add")}
          </Button>
        </div>
      </div>

      <Paper className={classes.listPaper} variant="outlined" onScroll={handleScroll}>
        <div className={classes.gridHeader}>
          <span>Cor</span>
          <span>Nome da Tag</span>
          <span>Qtd. de Contatos</span>
          <span style={{ textAlign: "right" }}>Ações</span>
        </div>
        <div className={classes.cardContainer}>
          {tags.length === 0 && !loading && (
            <div style={{ padding: 20 }}>
              {i18n.t("tags.noTagsFound") || "Nenhuma tag encontrada"}
            </div>
          )}
          {tags.map((tag) => (
            <div
              key={tag.id}
              className={classes.card}
              style={{ "--tag-color": tag.color || "#777" }}
              title={tag.name}
            >
              <span><span className={classes.colorDot} aria-label="Cor da etiqueta" /></span>
              <span className={classes.label}>{tag.name}</span>
              <span className={classes.contactsSection}>{tag?.contacts?.length || 0} contatos</span>
              <div className={classes.actions}>
                <IconButton
                  size="small"
                  className={classes.editButton}
                  onClick={() => handleEditTag(tag)}
                  aria-label={`Editar tag ${tag.name}`}
                >
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  className={classes.deleteButton}
                  onClick={() => {
                    setConfirmModalOpen(true);
                    setDeletingTag(tag);
                  }}
                  aria-label={`Excluir tag ${tag.name}`}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ width: "100%", textAlign: "center", padding: 20 }}>
              Carregando...
            </div>
          )}
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Tags;
